import { useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useAdminAuth } from '../lib/auth-context';
import { useCommandPaletteStore } from '../stores/command-palette-store';
import { useAdminSearch } from '../lib/use-admin-search';
import { readRecents, recordRecent, type PaletteHit } from '../lib/palette-recents';
import { NAV_GROUPS, visibleGroups } from './nav-model';
import { PALETTE_SECTIONS } from './palette-sections';
import { PERMS } from '../../rbac/permissions.js';

// Maps a stored recent's `perm` discriminator back to the bit that gates it.
// 'PAGE' (nav pages) has no PERMS bit — its visibility is re-checked against
// the current `pages` list instead. Anything else unrecognized (including a
// pre-perm-field legacy entry, which `readRecents` already drops) is treated
// as not renderable.
const PERM_BITS: Record<string, bigint> = PERMS;

function isRecentVisible(hit: PaletteHit, hasPerm: (b: bigint) => boolean, pageTos: Set<string>) {
  if (hit.perm === 'PAGE') return pageTos.has(hit.to);
  // OWN properties only. A stored `perm` naming an Object.prototype key
  // ('toString', 'constructor', 'valueOf') would otherwise resolve to a
  // function, survive an `=== undefined` check, and blow up `bitmap & fn`
  // with a TypeError inside render — permanently, since the value lives in
  // localStorage. The bigint check is the second belt on the same braces.
  const bit = Object.prototype.hasOwnProperty.call(PERMS, hit.perm)
    ? PERM_BITS[hit.perm]
    : undefined;
  if (typeof bit !== 'bigint') return false;
  return hasPerm(bit);
}

// No Dialog/Overlay primitive exists in @heyroot/ui (only Drawer and
// BottomSheet), so this uses a fixed overlay + backdrop — the same shape
// apps/backoffice/src/components/command-palette/CommandPalette.tsx settled on.

interface Section {
  key: string;
  label: string;
  rows: PaletteHit[];
}

export function CommandPalette() {
  const { open, close } = useCommandPaletteStore();
  const openPalette = useCommandPaletteStore((s) => s.openPalette);
  const { hasPerm } = useAdminAuth();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef: RefObject<HTMLDivElement | null> = useRef(null);

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const { results, isLoading, error } = useAdminSearch(query);

  // Ctrl/Cmd+K toggles from anywhere; Esc closes.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (useCommandPaletteStore.getState().open) close();
        else openPalette();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [close, openPalette]);

  // Reset per-open state and focus the input.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      inputRef.current?.focus();
    }
  }, [open]);

  // A new query means a new result list: the cursor goes back to the top match
  // rather than staying on whatever ordinal it held over the previous list.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Minimal Tab-loop focus trap, mirroring
  // apps/backoffice/src/components/command-palette/CommandPalette.tsx:192-218.
  // The listbox option rows deliberately stay OUT of the Tab order (arrow
  // keys move through results instead), so the trap only cycles among the
  // dialog's other focusable chrome (currently just the input) rather than
  // the results.
  useEffect(() => {
    if (!open) return;
    function handleTab(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const container = containerRef.current;
      if (!container) return;
      const focusables = Array.from(
        container.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]',
        ),
      ).filter((el) => el.offsetParent !== null && el.tabIndex !== -1);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      // Focus can sit OUTSIDE the dialog even while it is open: clicking a
      // non-focusable row of dialog chrome (a section header) drops focus to
      // <body>, and from there neither branch below matches, so Tab would
      // walk into the page behind the modal. Pull it back in first.
      if (!container.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handleTab);
    return () => document.removeEventListener('keydown', handleTab);
  }, [open]);

  const pages: PaletteHit[] = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    return visibleGroups(NAV_GROUPS, hasPerm)
      .flatMap((g) =>
        g.items.map((i) => ({
          id: i.to,
          label: i.label,
          subtitle: g.label,
          to: i.to,
          perm: 'PAGE',
        })),
      )
      .filter((p) => !trimmed || p.label.toLowerCase().includes(trimmed));
  }, [query, hasPerm]);

  const pageTos = useMemo(() => new Set(pages.map((p) => p.to)), [pages]);

  const sections: Section[] = useMemo(() => {
    const out: Section[] = [];
    if (!query.trim()) {
      const recents = readRecents().filter((r) => isRecentVisible(r, hasPerm, pageTos));
      if (recents.length) out.push({ key: 'recent', label: 'Recent', rows: recents });
    }
    if (pages.length) out.push({ key: 'pages', label: 'Pages', rows: pages });
    for (const section of PALETTE_SECTIONS) {
      const rows = results[section.key];
      if (!rows?.length) continue;
      out.push({
        key: section.key,
        label: section.label,
        rows: rows.map((r) => ({ ...r, perm: section.perm })),
      });
    }
    return out;
  }, [query, pages, results, hasPerm, pageTos]);

  const flatRows = useMemo(() => sections.flatMap((s) => s.rows), [sections]);

  // Keep the cursor inside the list as results change under it.
  useEffect(() => {
    setActiveIndex((i) => (flatRows.length === 0 ? 0 : Math.min(i, flatRows.length - 1)));
  }, [flatRows.length]);

  function choose(hit: PaletteHit) {
    recordRecent(hit);
    close();
    void navigate({ to: hit.to });
  }

  // Refs so the document-level key handler below reads the CURRENT cursor and
  // choose() without re-subscribing on every arrow press.
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;
  const chooseRef = useRef(choose);
  chooseRef.current = choose;
  const flatRowsRef = useRef(flatRows);
  flatRowsRef.current = flatRows;

  // Esc / arrow / Enter navigation lives on `document`, not on the dialog's
  // own onKeyDown: clicking a non-focusable part of the dialog (e.g. a
  // section header) moves focus to <body>, and a container-scoped React
  // handler then stops seeing keystrokes at all — the palette would become
  // un-closable by keyboard and stop responding to the arrows.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      const rows = flatRowsRef.current;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (rows.length ? (i + 1) % rows.length : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (rows.length ? (i - 1 + rows.length) % rows.length : 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const hit = rows[activeIndexRef.current];
        if (hit) chooseRef.current(hit);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" aria-hidden="true" onClick={() => close()} />
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className="fixed left-1/2 top-24 z-50 w-[min(36rem,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-control border border-border bg-surface-elevated shadow-lg"
      >
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <Search aria-hidden="true" className="h-4 w-4 shrink-0 text-muted" />
          {/* eslint-disable-next-line @heyroot/heyroot-ui/no-one-off-ui -- design-exception: borderless combobox inside an overlay; Input primitive carries its own chrome */}
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-results"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tenants, business types, pages…"
            className="w-full bg-transparent text-body-sm text-primary placeholder:text-muted focus:outline-none"
          />
          <kbd className="shrink-0 rounded-control bg-surface-sunken px-1.5 py-0.5 text-caption text-muted">
            Esc
          </kbd>
        </div>

        <div id="palette-results" role="listbox" className="max-h-80 overflow-y-auto py-1">
          {error && <p className="px-3 py-6 text-center text-body-sm text-muted">{error}</p>}

          {!error && flatRows.length === 0 && (
            <p className="px-3 py-6 text-center text-body-sm text-muted">
              {isLoading ? 'Searching…' : 'No matches'}
            </p>
          )}

          {sections.map((section) => {
            const offset = sections
              .slice(0, sections.indexOf(section))
              .reduce((n, s) => n + s.rows.length, 0);
            return (
              <div key={section.key}>
                <p className="px-3 pt-2 pb-1 text-eyebrow uppercase text-muted">{section.label}</p>
                {section.rows.map((row, i) => {
                  const index = offset + i;
                  const isActive = index === activeIndex;
                  return (
                    // tabIndex=-1: option rows stay OUT of the Tab order — arrow
                    // keys move through results, Tab only cycles the dialog's
                    // other chrome (see the focus-trap effect above).
                    <button
                      key={`${section.key}:${row.id}`}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      tabIndex={-1}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => choose(row)}
                      className={
                        isActive
                          ? 'flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left bg-surface-sunken'
                          : 'flex w-full flex-col items-start gap-0.5 px-3 py-1.5 text-left hover:bg-surface-sunken'
                      }
                    >
                      <span className="text-body-sm text-primary">{row.label}</span>
                      {row.subtitle && (
                        <span className="text-caption text-muted">{row.subtitle}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
